import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FaSave, FaArrowLeft, FaTrash, FaCheck, FaTimes, FaEdit } from 'react-icons/fa';

export default function AdminComparatorSpecs() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const { productId } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [specs, setSpecs] = useState({
    modele: '',
    marque: '',
    type_equipement: '',
    couple_knm: '',
    vitesse_rotation_rpm: '',
    poussee_traction_kn: '',
    profondeur_max_m: '',
    diametre_max_mm: '',
    methodes_compatibles: [],
    poids_kg: '',
    poids_transport: '',
    dimensions_transport: '',
    motorisation: '',
    puissance_kw: '',
    options_disponibles: [],
    prix_indicatif: '',
    devise: 'EUR'
  });
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hasExistingSpecs, setHasExistingSpecs] = useState(false);
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    fetchProduct();
    fetchEquipmentTypes();
    fetchMethods();
    fetchSpecs();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      // Pre-fill some fields from product
      setSpecs(prev => ({
        ...prev,
        marque: response.data.brand || '',
        modele: response.data.model || ''
      }));
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produit non trouvé');
      navigate('/admin/comparator');
    }
  };

  const fetchSpecs = async () => {
    try {
      const response = await axios.get(`${API}/comparator/specs/${productId}`);
      if (response.data.has_specs) {
        setHasExistingSpecs(true);
        const existingSpecs = response.data.specs;
        setSpecs({
          modele: existingSpecs.modele || '',
          marque: existingSpecs.marque || '',
          type_equipement: existingSpecs.type_equipement || '',
          couple_knm: existingSpecs.couple_knm || '',
          vitesse_rotation_rpm: existingSpecs.vitesse_rotation_rpm || '',
          poussee_traction_kn: existingSpecs.poussee_traction_kn || '',
          profondeur_max_m: existingSpecs.profondeur_max_m || '',
          diametre_max_mm: existingSpecs.diametre_max_mm || '',
          methodes_compatibles: existingSpecs.methodes_compatibles || [],
          poids_kg: existingSpecs.poids_kg || '',
          poids_transport: existingSpecs.poids_transport || '',
          dimensions_transport: existingSpecs.dimensions_transport || '',
          motorisation: existingSpecs.motorisation || '',
          puissance_kw: existingSpecs.puissance_kw || '',
          options_disponibles: existingSpecs.options_disponibles || [],
          prix_indicatif: existingSpecs.prix_indicatif || '',
          devise: existingSpecs.devise || 'EUR'
        });
      }
    } catch (error) {
      console.error('Error fetching specs:', error);
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/comparator/equipment-types`);
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
    }
  };

  const fetchMethods = async () => {
    try {
      const response = await axios.get(`${API}/comparator/methods`);
      setMethods(response.data);
    } catch (error) {
      console.error('Error fetching methods:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setSpecs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMethodToggle = (methodId) => {
    setSpecs(prev => ({
      ...prev,
      methodes_compatibles: prev.methodes_compatibles.includes(methodId)
        ? prev.methodes_compatibles.filter(m => m !== methodId)
        : [...prev.methodes_compatibles, methodId]
    }));
  };

  const addOption = () => {
    if (newOption.trim() && !specs.options_disponibles.includes(newOption.trim())) {
      setSpecs(prev => ({
        ...prev,
        options_disponibles: [...prev.options_disponibles, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const removeOption = (option) => {
    setSpecs(prev => ({
      ...prev,
      options_disponibles: prev.options_disponibles.filter(o => o !== option)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert numeric strings to numbers
      const specsToSave = {
        modele: specs.modele || null,
        marque: specs.marque || null,
        type_equipement: specs.type_equipement || null,
        couple_knm: specs.couple_knm ? parseFloat(specs.couple_knm) : null,
        vitesse_rotation_rpm: specs.vitesse_rotation_rpm ? parseFloat(specs.vitesse_rotation_rpm) : null,
        poussee_traction_kn: specs.poussee_traction_kn ? parseFloat(specs.poussee_traction_kn) : null,
        profondeur_max_m: specs.profondeur_max_m ? parseFloat(specs.profondeur_max_m) : null,
        diametre_max_mm: specs.diametre_max_mm ? parseFloat(specs.diametre_max_mm) : null,
        methodes_compatibles: specs.methodes_compatibles,
        poids_kg: specs.poids_kg ? parseFloat(specs.poids_kg) : null,
        poids_transport: specs.poids_transport || null,
        dimensions_transport: specs.dimensions_transport || null,
        motorisation: specs.motorisation || null,
        puissance_kw: specs.puissance_kw ? parseFloat(specs.puissance_kw) : null,
        options_disponibles: specs.options_disponibles,
        prix_indicatif: specs.prix_indicatif ? parseFloat(specs.prix_indicatif) : null,
        devise: specs.devise
      };

      await axios.post(`${API}/comparator/specs`, {
        product_id: productId,
        specs: specsToSave
      });

      toast.success(i18n.language === 'fr' ? 'Spécifications enregistrées !' : 'Specifications saved!');
      setHasExistingSpecs(true);
    } catch (error) {
      console.error('Error saving specs:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(i18n.language === 'fr' ? 'Supprimer ces spécifications ?' : 'Delete these specifications?')) {
      return;
    }

    try {
      await axios.delete(`${API}/comparator/specs/${productId}`);
      toast.success(i18n.language === 'fr' ? 'Spécifications supprimées' : 'Specifications deleted');
      navigate('/admin/comparator');
    } catch (error) {
      console.error('Error deleting specs:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting');
    }
  };

  if (!product) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/comparator')}>
              <FaArrowLeft className="mr-2" />
              {i18n.language === 'fr' ? 'Retour' : 'Back'}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#0A2540]">
                {i18n.language === 'fr' ? 'Spécifications Techniques' : 'Technical Specifications'}
              </h1>
              <p className="text-gray-500">{product.name_fr}</p>
            </div>
          </div>
          {hasExistingSpecs && (
            <Button variant="destructive" onClick={handleDelete}>
              <FaTrash className="mr-2" />
              {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          )}
        </div>

        {/* Product Preview */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {product.images?.[0] && (
                <img src={product.images[0]} alt={product.name_fr} className="w-20 h-20 object-cover rounded-lg" />
              )}
              <div>
                <h3 className="font-bold text-lg">{product.name_fr}</h3>
                <p className="text-sm text-gray-500">{product.brand} {product.model}</p>
                <Badge variant={hasExistingSpecs ? "default" : "secondary"} className={hasExistingSpecs ? "bg-green-500" : ""}>
                  {hasExistingSpecs 
                    ? (i18n.language === 'fr' ? 'Specs complètes' : 'Specs complete')
                    : (i18n.language === 'fr' ? 'Sans specs' : 'No specs')}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Specifications Form */}
        <div className="space-y-6">
          {/* Identity */}
          <Card>
            <CardHeader className="bg-[#635BFF]/5">
              <CardTitle className="text-[#635BFF]">📋 {i18n.language === 'fr' ? 'Identité' : 'Identity'}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{i18n.language === 'fr' ? 'Marque' : 'Brand'}</Label>
                <Input 
                  value={specs.marque} 
                  onChange={(e) => handleInputChange('marque', e.target.value)}
                  placeholder="Ex: Soilmec, Bauer, Liebherr"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Modèle' : 'Model'}</Label>
                <Input 
                  value={specs.modele} 
                  onChange={(e) => handleInputChange('modele', e.target.value)}
                  placeholder="Ex: SR-45, BG 28"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? "Type d'équipement" : 'Equipment Type'}</Label>
                <Select value={specs.type_equipement} onValueChange={(val) => handleInputChange('type_equipement', val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {i18n.language === 'fr' ? type.label_fr : type.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Performance */}
          <Card>
            <CardHeader className="bg-[#00D4FF]/5">
              <CardTitle className="text-[#00D4FF]">⚡ {i18n.language === 'fr' ? 'Performance' : 'Performance'}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{i18n.language === 'fr' ? 'Couple (kNm)' : 'Torque (kNm)'}</Label>
                <Input 
                  type="number"
                  step="0.1"
                  value={specs.couple_knm} 
                  onChange={(e) => handleInputChange('couple_knm', e.target.value)}
                  placeholder="Ex: 280"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Vitesse rotation (rpm)' : 'Rotation speed (rpm)'}</Label>
                <Input 
                  type="number"
                  value={specs.vitesse_rotation_rpm} 
                  onChange={(e) => handleInputChange('vitesse_rotation_rpm', e.target.value)}
                  placeholder="Ex: 35"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Poussée/Traction (kN)' : 'Push/Pull (kN)'}</Label>
                <Input 
                  type="number"
                  value={specs.poussee_traction_kn} 
                  onChange={(e) => handleInputChange('poussee_traction_kn', e.target.value)}
                  placeholder="Ex: 450"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Profondeur max (m)' : 'Max depth (m)'}</Label>
                <Input 
                  type="number"
                  value={specs.profondeur_max_m} 
                  onChange={(e) => handleInputChange('profondeur_max_m', e.target.value)}
                  placeholder="Ex: 65"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Diamètre max (mm)' : 'Max diameter (mm)'}</Label>
                <Input 
                  type="number"
                  value={specs.diametre_max_mm} 
                  onChange={(e) => handleInputChange('diametre_max_mm', e.target.value)}
                  placeholder="Ex: 2500"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Puissance (kW)' : 'Power (kW)'}</Label>
                <Input 
                  type="number"
                  value={specs.puissance_kw} 
                  onChange={(e) => handleInputChange('puissance_kw', e.target.value)}
                  placeholder="Ex: 450"
                />
              </div>
            </CardContent>
          </Card>

          {/* Methods */}
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle className="text-green-700">🔧 {i18n.language === 'fr' ? 'Méthodes Compatibles' : 'Compatible Methods'}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {methods.map(method => (
                  <div 
                    key={method.id}
                    onClick={() => handleMethodToggle(method.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                      specs.methodes_compatibles.includes(method.id)
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      specs.methodes_compatibles.includes(method.id)
                        ? 'bg-green-500 border-green-500'
                        : 'border-gray-300'
                    }`}>
                      {specs.methodes_compatibles.includes(method.id) && (
                        <FaCheck className="text-white text-xs" />
                      )}
                    </div>
                    <span className="text-sm">{i18n.language === 'fr' ? method.label_fr : method.label_en}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Logistics */}
          <Card>
            <CardHeader className="bg-orange-50">
              <CardTitle className="text-orange-700">🚚 {i18n.language === 'fr' ? 'Transport & Logistique' : 'Transport & Logistics'}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{i18n.language === 'fr' ? 'Poids (kg)' : 'Weight (kg)'}</Label>
                <Input 
                  type="number"
                  value={specs.poids_kg} 
                  onChange={(e) => handleInputChange('poids_kg', e.target.value)}
                  placeholder="Ex: 85000"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Conditions de transport' : 'Transport conditions'}</Label>
                <Input 
                  value={specs.poids_transport} 
                  onChange={(e) => handleInputChange('poids_transport', e.target.value)}
                  placeholder="Ex: Convoi exceptionnel"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Motorisation' : 'Engine type'}</Label>
                <Input 
                  value={specs.motorisation} 
                  onChange={(e) => handleInputChange('motorisation', e.target.value)}
                  placeholder="Ex: Caterpillar C18"
                />
              </div>
            </CardContent>
          </Card>

          {/* Options */}
          <Card>
            <CardHeader className="bg-purple-50">
              <CardTitle className="text-purple-700">⚙️ {i18n.language === 'fr' ? 'Options Disponibles' : 'Available Options'}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex gap-2 mb-4">
                <Input 
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder={i18n.language === 'fr' ? 'Ajouter une option...' : 'Add an option...'}
                  onKeyPress={(e) => e.key === 'Enter' && addOption()}
                />
                <Button onClick={addOption} variant="outline">
                  {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {specs.options_disponibles.map((option, index) => (
                  <Badge key={index} variant="secondary" className="px-3 py-1.5 flex items-center gap-2">
                    {option}
                    <button onClick={() => removeOption(option)} className="hover:text-red-500">
                      <FaTimes />
                    </button>
                  </Badge>
                ))}
                {specs.options_disponibles.length === 0 && (
                  <p className="text-gray-400 text-sm italic">
                    {i18n.language === 'fr' ? 'Aucune option ajoutée' : 'No options added'}
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {i18n.language === 'fr' 
                  ? 'Exemples: Pompe à boue, Marteau hydraulique, Instrumentation, GPS, Télémétrie'
                  : 'Examples: Mud pump, Hydraulic hammer, Instrumentation, GPS, Telemetry'}
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/comparator')}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#635BFF] hover:bg-[#5248d9]">
              {saving ? (
                <span className="animate-pulse">{i18n.language === 'fr' ? 'Enregistrement...' : 'Saving...'}</span>
              ) : (
                <>
                  <FaSave className="mr-2" />
                  {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
